/**
 * PendingAdminsSection — متابعة المشرفين الجدد المُعلَّقين بانتظار التفعيل.
 * Extracted from AccountSettings.js
 */
import { motion } from 'framer-motion';
import {
  Clock, RefreshCw, CheckCircle, Loader2, User, Send, Phone,
} from 'lucide-react';
import { Button } from '../../../components/ui/button';

export default function PendingAdminsSection({
  language,
  loadingPending,
  pendingAdmins,
  fetchPendingAdmins,
  resendActivationEmail,
  resendingEmail,
}) {
  return (
    <div
      className="bg-white/[0.02] backdrop-blur-xl rounded-3xl border border-white/10 p-6"
      data-testid="pending-admins-section"
    >
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold text-white flex items-center gap-2">
          <Clock className="h-5 w-5 text-amber-400" />
          {language === 'ar' ? 'متابعة المشرفين الجدد' : 'Follow Up New Admins'}
        </h2>
        <Button
          onClick={fetchPendingAdmins}
          variant="outline"
          size="sm"
          className="border-white/10 text-white hover:bg-white/5"
        >
          <RefreshCw className="h-4 w-4 ml-2" />
          {language === 'ar' ? 'تحديث' : 'Refresh'}
        </Button>
      </div>

      <div className="mb-6 bg-white/5 rounded-xl p-4 border border-white/10">
        <p className="text-sm text-[#808080] mb-3">
          {language === 'ar' ? 'شرح الحالات:' : 'Status Explanation:'}
        </p>
        <div className="flex flex-wrap gap-4">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-amber-500" />
            <span className="text-xs text-[#808080]">
              {language === 'ar' ? 'بانتظار التفعيل' : 'Pending Activation'}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-emerald-500" />
            <span className="text-xs text-[#808080]">
              {language === 'ar' ? 'تم التفعيل' : 'Activated'}
            </span>
          </div>
        </div>
      </div>

      {loadingPending ? (
        <div className="flex justify-center items-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-amber-400" />
        </div>
      ) : pendingAdmins.length === 0 ? (
        <div className="text-center py-12 text-[#808080]">
          <CheckCircle className="h-12 w-12 mx-auto mb-3 text-emerald-500/50" />
          <p className="text-lg">
            {language === 'ar' ? 'لا يوجد مشرفين معلقين' : 'No Pending Admins'}
          </p>
          <p className="text-sm mt-1">
            {language === 'ar'
              ? 'جميع المشرفين قاموا بتفعيل حساباتهم'
              : 'All admins have activated their accounts'}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {pendingAdmins.map((admin) => {
            // ---- compute expiry status once for layout reuse ----
            let expiryNode = null;
            if (admin.token_expires_at) {
              const exp = new Date(admin.token_expires_at);
              const now = new Date();
              const diffMs = exp - now;
              const expired = diffMs <= 0;
              const hoursLeft = Math.max(0, Math.floor(diffMs / (1000 * 60 * 60)));
              const minutesLeft = Math.max(0, Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60)));
              expiryNode = (
                <span className={expired ? 'text-red-400' : 'text-amber-300'}>
                  {expired
                    ? (language === 'ar' ? 'الرابط منتهي — أعد الإرسال' : 'Link expired — resend')
                    : (language === 'ar'
                        ? `متبقي ${hoursLeft}س ${minutesLeft}د`
                        : `${hoursLeft}h ${minutesLeft}m left`)}
                </span>
              );
            }

            return (
              <motion.div
                key={admin.id || admin.email}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-[#0a0a0a] rounded-xl p-4 border border-amber-500/20"
                data-testid={`pending-admin-${admin.id}`}
              >
                {/* ===== Header row: avatar+name (start) | badge + resend btn (end) ===== */}
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="flex items-center gap-4 min-w-0">
                    <div className="w-12 h-12 shrink-0 rounded-full bg-amber-500/20 flex items-center justify-center">
                      <User className="h-6 w-6 text-amber-400" />
                    </div>
                    <div className="min-w-0">
                      <p className="font-semibold text-white truncate">
                        {admin.name || `${admin.first_name || ''} ${admin.last_name || ''}`.trim() || 'بدون اسم'}
                      </p>
                      <p className="text-sm text-[#808080] truncate">{admin.email}</p>
                      {admin.username && (
                        <p className="text-xs text-[#606060] truncate" dir="ltr">@{admin.username}</p>
                      )}
                    </div>
                  </div>

                  {/* Right cluster — status badge + resend btn stay on ONE row */}
                  <div className="flex items-center gap-2 shrink-0">
                    <span className={`text-xs px-3 py-1.5 rounded-full whitespace-nowrap ${admin.status === 'pending_activation'
                      ? 'bg-amber-500/20 text-amber-400'
                      : 'bg-emerald-500/20 text-emerald-400'}`}>
                      {admin.status === 'pending_activation'
                        ? (language === 'ar' ? 'بانتظار التفعيل' : 'Pending')
                        : (language === 'ar' ? 'مفعّل' : 'Active')}
                    </span>

                    {admin.status === 'pending_activation' && (
                      <Button
                        onClick={() => resendActivationEmail(admin.id)}
                        disabled={resendingEmail === admin.id}
                        size="sm"
                        className="bg-amber-600 hover:bg-amber-700 text-white h-8"
                        data-testid={`resend-btn-${admin.id}`}
                      >
                        {resendingEmail === admin.id ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <>
                            <Send className="h-3.5 w-3.5 ml-1" />
                            {language === 'ar' ? 'إعادة الإرسال' : 'Resend'}
                          </>
                        )}
                      </Button>
                    )}
                  </div>
                </div>

                {/* ===== Footer row: meta (phone | expiry | created | sent_count) - all on one line ===== */}
                <div className="mt-3 pt-3 border-t border-white/5 flex items-center justify-between flex-wrap gap-x-4 gap-y-1 text-xs text-[#808080]">
                  <div className="flex items-center gap-x-4 gap-y-1 flex-wrap">
                    {admin.phone && (
                      <span dir="ltr" className="flex items-center gap-1">
                        <Phone className="h-3 w-3" />{admin.phone}
                      </span>
                    )}
                    {expiryNode}
                    {admin.created_at && (
                      <span className="text-[#606060] flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {new Date(admin.created_at).toLocaleDateString('ar-AE', {
                          year: 'numeric', month: 'short', day: 'numeric',
                        })}
                      </span>
                    )}
                  </div>
                  {admin.email_sent_count > 0 && (
                    <span className="text-[#606060]">
                      {language === 'ar' ? `تم الإرسال ${admin.email_sent_count} مرة` : `Sent ${admin.email_sent_count} times`}
                    </span>
                  )}
                </div>
              </motion.div>
            );
          })}
        </div>
      )}
    </div>
  );
}
